import { useState } from "react";
import { ScanSearch, PencilLine } from "lucide-react";
import { BREEDS, GENDERS } from "@/lib/horseOptions";
import { REG_ROWS, fromScan, displayReg } from "@/lib/regMap";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const OPTS = { gender: GENDERS, breed: BREEDS };
const REQUIRED = ["registered_name", "registered_number"];

function Row({ k, label, value, found, onChange, error }) {
  const diff = !!found && !!value && value !== found;
  const id = `apply-confirm-${k}`;
  return (
    <div data-testid={`${id}-row`} className={`rounded-lg border p-2.5 ${diff ? "border-amber-400 bg-amber-50" : error ? "border-red-400 bg-white" : "border-stone-200 bg-white"}`}>
      <label htmlFor={id} className="text-xs font-semibold text-stone-700">{label}{REQUIRED.includes(k) && <span className="text-red-600"> *</span>}</label>
      {OPTS[k] ? (
        <select id={id} data-testid={id} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full h-11 rounded-md border border-stone-300 bg-white px-2 text-base">
          <option value="">Select</option>{OPTS[k].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      ) : <Input id={id} data-testid={id} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 h-11 text-base" />}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      {diff && (
        <p className="mt-1 text-xs text-amber-900">Papers say <strong>{displayReg(k, found)}</strong>{" "}
          <button type="button" data-testid={`${id}-use`} onClick={() => onChange(found)} className="font-semibold underline">Use this</button></p>
      )}
    </div>
  );
}

// scan = recognised AI result, or null for manual entry. Confirmation is always required.
export function RegistrationConfirm({ scan, horse, onConfirm }) {
  const found = scan ? fromScan(scan) : {};
  const [vals, setVals] = useState(() => Object.fromEntries(REG_ROWS.map(([k]) => [k, horse[k] || found[k] || ""])));
  const [tried, setTried] = useState(false);
  const missing = REQUIRED.filter((k) => !String(vals[k] || "").trim());
  const confirm = () => { setTried(true); if (!missing.length) onConfirm(Object.fromEntries(Object.entries(vals).map(([k, v]) => [k, String(v || "").trim()]))); };
  return (
    <section data-testid="apply-confirm-panel" className="rounded-xl border-2 border-amber-700 bg-amber-50/40 p-3 sm:p-4 space-y-3">
      <h3 className="font-semibold text-stone-900 flex items-center gap-2">
        {scan ? <ScanSearch className="h-5 w-5 text-amber-800" /> : <PencilLine className="h-5 w-5 text-amber-800" />}Please Confirm Your Horse's Information
      </h3>
      <p data-testid="apply-confirm-mode" className="text-sm text-stone-600">{scan
        ? `We read this from your ${scan.registry || "registration"} papers. Check each value, correct anything that's wrong and fill in what's missing.`
        : "Enter your horse's registration information from the papers. Your uploaded documents stay attached for our review."}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {REG_ROWS.map(([k, label]) => <Row key={k} k={k} label={label} value={vals[k]} found={found[k]} error={tried && missing.includes(k) ? "Required" : null} onChange={(v) => setVals({ ...vals, [k]: v })} />)}
      </div>
      <Button type="button" data-testid="apply-confirm-save" onClick={confirm} className="w-full sm:w-auto h-12 rounded-full bg-amber-800 hover:bg-amber-900 text-white">Confirm Horse Information &amp; Continue</Button>
    </section>
  );
}
